import { useState } from 'react';
import { ActionIcon, Badge, Group, Modal, Paper, ScrollArea, Text, Tooltip } from '@mantine/core';
import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { CCMSDataTable, ListPagination } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import type { Row } from '../types';

interface Props {
  sourceLabel: string;
  hasColumns: boolean;
  hasMatches: boolean;
  rows: Row[];                    // current page only
  columns: TableColumn<Row>[];
  page: number;
  pageSize: number;
  totalItems: number;
  /** Server sources only — true while the current page is being refetched. */
  loading?: boolean;
  onSort: (config: SortConfig | null) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/** Step 4 — the paginated, sortable preview of exactly what will be exported. */
export function PreviewTable({
  sourceLabel, hasColumns, hasMatches, rows, columns, page, pageSize, totalItems,
  loading, onSort, onPageChange, onPageSizeChange,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  const canExpand = hasColumns && (hasMatches || Boolean(loading));

  /** Table + pagination, rendered either inline or inside the full-screen modal (never both). */
  const renderTable = (fullScreen: boolean) => (
    <>
      <ScrollArea.Autosize mah={fullScreen ? 'calc(100vh - 220px)' : undefined}>
        <div style={{ minWidth: fullScreen ? undefined : 900 }}>
          <CCMSDataTable
            data={rows}
            columns={columns}
            sortable
            loading={loading && rows.length === 0}
            onSort={onSort}
            striped
            highlightOnHover
          />
        </div>
      </ScrollArea.Autosize>
      <ListPagination
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showPageSize
      />
    </>
  );

  return (
    <Paper withBorder p="md" radius="md" h="100%">
      <Group justify="space-between" mb="sm">
        <Text fw={600}>4. Live preview</Text>
        <Group gap="xs">
          <Badge variant="light">{sourceLabel}</Badge>
          <Tooltip label="Open full screen">
            <ActionIcon
              variant="subtle"
              onClick={() => setExpanded(true)}
              disabled={!canExpand}
              aria-label="Open preview full screen"
            >
              <IconArrowsMaximize size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {!hasColumns ? (
        <Text c="dimmed" size="sm" py="xl" ta="center">Select at least one column to see a preview.</Text>
      ) : !hasMatches && !loading ? (
        <Text c="dimmed" size="sm" py="xl" ta="center">No records match the current filters.</Text>
      ) : expanded ? (
        <Text c="dimmed" size="sm" py="xl" ta="center">Preview is open in full screen.</Text>
      ) : (
        renderTable(false)
      )}

      <Modal
        opened={expanded}
        onClose={() => setExpanded(false)}
        fullScreen
        padding="md"
        title={
          <Group gap="xs">
            <Text fw={600}>Live preview</Text>
            <Badge variant="light">{sourceLabel}</Badge>
          </Group>
        }
        closeButtonProps={{ 'aria-label': 'Close full screen' }}
      >
        {expanded && renderTable(true)}
      </Modal>
    </Paper>
  );
}